import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PortalShell from "@/components/PortalShell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function ChangePassword() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role === "admin" ? "admin" : "client";

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const validate = () => {
    if (newPassword.length < 8) return "Le nouveau mot de passe doit contenir au moins 8 caractères.";
    if (newPassword !== confirmPassword) return "La confirmation ne correspond pas au nouveau mot de passe.";
    if (newPassword === currentPassword) return "Le nouveau mot de passe doit être différent de l'ancien.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const strength = (() => {
    if (!newPassword) return { label: "—", color: "text-neutral-500", bar: "w-0 bg-neutral-700" };
    const hasUpper = /[A-Z]/.test(newPassword);
    const hasLower = /[a-z]/.test(newPassword);
    const hasNum = /[0-9]/.test(newPassword);
    const hasSpecial = /[^A-Za-z0-9]/.test(newPassword);
    const score = (newPassword.length >= 12 ? 1 : 0) + hasUpper + hasLower + hasNum + hasSpecial;
    if (score <= 2) return { label: "Faible", color: "text-red-400", bar: "w-1/4 bg-red-500" };
    if (score === 3) return { label: "Moyen", color: "text-amber-400", bar: "w-2/4 bg-amber-500" };
    if (score === 4) return { label: "Bon", color: "text-lime-400", bar: "w-3/4 bg-lime-500" };
    return { label: "Excellent", color: "text-emerald-400", bar: "w-full bg-emerald-500" };
  })();

  return (
    <PortalShell role={role}>
      <div className="max-w-2xl" data-testid="change-password-page">
        <div className="eyebrow mb-3">/ Sécurité du compte</div>
        <h1 className="font-display font-bold uppercase tracking-tight text-3xl md:text-4xl leading-[0.95]">
          Changer le<br /><span className="text-[#ffd600]">mot de passe</span>
        </h1>
        <p className="text-neutral-400 mt-4 max-w-xl">
          Renforcez la sécurité de votre compte en remplaçant régulièrement votre mot de passe. Minimum 8 caractères, idéalement avec majuscules, chiffres et caractères spéciaux.
        </p>

        {success && (
          <div
            data-testid="change-password-success"
            className="mt-8 border border-[#ffd600] bg-[#ffd600]/10 p-5 text-[#ffd600]"
          >
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-circle-check text-xl" />
              <div>
                <div className="font-display uppercase tracking-wider">Mot de passe modifié</div>
                <div className="text-sm text-neutral-200 mt-1">
                  Votre nouveau mot de passe est actif. Vous pouvez continuer à naviguer ou{" "}
                  <button
                    type="button"
                    onClick={() => navigate(role === "admin" ? "/admin" : "/espace-client")}
                    className="underline"
                  >
                    retourner au tableau de bord
                  </button>
                  .
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div
            data-testid="change-password-error"
            className="mt-8 border border-red-500/40 bg-red-500/10 p-5 text-red-300 flex items-start gap-3"
          >
            <i className="fa-solid fa-triangle-exclamation text-lg mt-0.5" />
            <div className="text-sm">{error}</div>
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-8 space-y-6" data-testid="change-password-form">
          {/* Current password */}
          <div>
            <label className="block font-mono-tech text-[10px] uppercase tracking-widest text-neutral-400 mb-2">
              Mot de passe actuel
            </label>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="input-current-password"
                className="w-full bg-[#0d0d0d] border border-[#262626] text-white px-4 py-3 pr-12 font-mono-tech text-sm focus:border-[#ffd600] focus:outline-none transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white"
                tabIndex={-1}
                data-testid="toggle-current-password"
              >
                <i className={showCurrent ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"} />
              </button>
            </div>
          </div>

          {/* New password */}
          <div>
            <label className="block font-mono-tech text-[10px] uppercase tracking-widest text-neutral-400 mb-2">
              Nouveau mot de passe
            </label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="input-new-password"
                className="w-full bg-[#0d0d0d] border border-[#262626] text-white px-4 py-3 pr-12 font-mono-tech text-sm focus:border-[#ffd600] focus:outline-none transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowNew((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white"
                tabIndex={-1}
                data-testid="toggle-new-password"
              >
                <i className={showNew ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"} />
              </button>
            </div>
            {/* Strength meter */}
            <div className="mt-2 flex items-center gap-3">
              <div className="flex-1 h-1 bg-neutral-800 overflow-hidden">
                <div className={`h-full transition-all ${strength.bar}`} />
              </div>
              <div className={`font-mono-tech text-[10px] uppercase tracking-widest ${strength.color}`} data-testid="password-strength">
                {strength.label}
              </div>
            </div>
          </div>

          {/* Confirm */}
          <div>
            <label className="block font-mono-tech text-[10px] uppercase tracking-widest text-neutral-400 mb-2">
              Confirmer le nouveau mot de passe
            </label>
            <input
              type={showNew ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="input-confirm-password"
              className="w-full bg-[#0d0d0d] border border-[#262626] text-white px-4 py-3 font-mono-tech text-sm focus:border-[#ffd600] focus:outline-none transition-colors"
            />
            {confirmPassword && confirmPassword !== newPassword && (
              <div className="mt-2 text-xs text-red-400 font-mono-tech">
                <i className="fa-solid fa-circle-xmark mr-1" /> Les mots de passe ne correspondent pas.
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-change-password"
              className="bg-[#ffd600] text-black font-display uppercase tracking-wider px-6 py-3 hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" /> Modification en cours…
                </>
              ) : (
                <>
                  <i className="fa-solid fa-key" /> Mettre à jour
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate(role === "admin" ? "/admin" : "/espace-client")}
              className="btn-ghost"
              data-testid="cancel-change-password"
            >
              Annuler
            </button>
          </div>
        </form>

        <div className="mt-12 border-t border-white/5 pt-6">
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
            Conseils sécurité
          </div>
          <ul className="text-sm text-neutral-400 space-y-2">
            <li>• Utilisez un mot de passe unique, jamais réutilisé ailleurs.</li>
            <li>• Idéalement 12 caractères et plus, avec majuscules, chiffres et caractères spéciaux.</li>
            <li>• Conservez-le dans un gestionnaire (1Password, Bitwarden, Apple Keychain…).</li>
            <li>• Ne le partagez jamais par email ou SMS.</li>
          </ul>
        </div>
      </div>
    </PortalShell>
  );
}
